/**
 * 「回滚到此快照」的**二次确认**对话框 —— `AC-N4.21` 的破坏性动作闸（端侧）
 *
 * 依据：**`DR-039` D5**（「回滚 = 用快照的 `payload` 走一次 `overwrite`」）·
 * `DR-039` **§4.3**（回滚前先落一份 ⇒ **回滚本身也可回滚**）·
 * `contract/snapshot.ts` 的 `RollbackSnapshotRequest` 文件头（**用户确认刻意不在契约里**）。
 *
 * ## 🔴 内容层**单独导出**（`RollbackConfirmBody`）—— 本项目已反复踩的那一课
 *
 * MUI `Dialog` 走 **Portal** ⇒ 子树被挂到 `document.body`，而端侧测试基座是
 * `renderToStaticMarkup`（**无 DOM**、不跑 effect）⇒ 直接断言"对话框内容在不在产物里"
 * 会得到**假红 / 假绿两种不可靠结果**（㉝ / `60` / `63` / `65` 批反复踩）。
 * ⇒ 内容层**全受控 + 单独导出**，外壳只管 `open` / 提交。
 * 并配**结构断言**钉住"外壳在静态渲染下不产出内容"。
 *
 * ## 🔴 为什么这段文案是本批最不能省的东西
 *
 * 回滚会**清空当前账本的全部业务数据**（它是 `overwrite`）。用户敢不敢点，取决于两句：
 * ① 「之后的数据会消失」（如实说代价）；② 🔴 「回滚前会自动留一份」（**安全网** ——
 * `DR-039` §4.3 的实现事实）。**缺 ② ⇒ 用户以为回滚是单向的、不敢用；
 * 缺 ① ⇒ 用户以为无害、乱点**。⇒ `features/snapshot.spec.ts` 对这两段**成对断言**。
 */

import { Alert, Box, Button, Dialog, Stack, Typography } from '@mui/material';
import {
  ROLLBACK_CANCEL,
  ROLLBACK_CONFIRM,
  ROLLBACK_DIALOG_TITLE,
  ROLLBACK_FAILED_PREFIX,
  ROLLBACK_IMPACT_NOTE,
  ROLLBACK_SAFETY_NOTE,
  SNAPSHOT_ROLLBACK_BUSY,
} from '../features/snapshot-labels.js';

/**
 * 内容层 props —— 🔴 **全受控**（父级持全部状态）⇒ 可被静态渲染精确断言。
 *
 * ⚠️ 注意这里**没有** `canRun` / `enabled` 这类"要不要给确认"的开关 ——
 * 回滚**恒**需确认（判据出自 `DR-039` D5 + 契约文件头，见 `features/snapshot.ts` 文件头）。
 * 加一个开关就等于允许"某条件下不给确认"，那是端侧擅自放宽破坏性动作的闸门。
 */
export interface RollbackConfirmBodyProps {
  /** 正在提交 */
  busy: boolean;
  /** 提交失败的服务端 `message`（`null` = 没失败） */
  error: string | null;
  /** 用户确认 */
  onConfirm: () => void;
  /** 取消 / 关闭 */
  onCancel: () => void;
}

export function RollbackConfirmBody(props: RollbackConfirmBodyProps) {
  return (
    <Box sx={{ p: 3 }} data-testid="rollback-confirm-body">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {ROLLBACK_DIALOG_TITLE}
      </Typography>

      {/* ① 代价（如实说：会覆盖、之后的数据会消失） */}
      <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="rollback-confirm-impact">
        {ROLLBACK_IMPACT_NOTE}
      </Alert>

      {/* ② 🔴 安全网（DR-039 §4.3 —— 缺这段用户就不敢点） */}
      <Alert severity="info" sx={{ mt: 1 }} data-testid="rollback-confirm-safety">
        {ROLLBACK_SAFETY_NOTE}
      </Alert>

      {props.error !== null ? (
        <Alert severity="error" sx={{ mt: 1.5 }} data-testid="rollback-confirm-error">
          {ROLLBACK_FAILED_PREFIX}
          {props.error}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
        <Button size="small" variant="outlined" onClick={props.onCancel} disabled={props.busy} data-testid="rollback-cancel">
          {ROLLBACK_CANCEL}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={props.onConfirm}
          disabled={props.busy}
          data-testid="rollback-confirm"
        >
          {props.busy ? SNAPSHOT_ROLLBACK_BUSY : ROLLBACK_CONFIRM}
        </Button>
      </Stack>
    </Box>
  );
}

// ─────────────────────────── 外壳 ───────────────────────────

/** 外壳 —— 只负责 `open` / 提交（内容全在 {@link RollbackConfirmBody}） */
export function RollbackConfirmDialog({
  open,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <RollbackConfirmBody busy={busy} error={error} onConfirm={onConfirm} onCancel={onClose} />
    </Dialog>
  );
}
